import { Currency } from "dinero.js"
import { useField, useFormikContext } from "formik"
import { DateTime } from "luxon"
import { Divider, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { CheckboxCard } from "../../../../../../shared/components/checkbox/CheckboxCard"
import { FormikDatePicker, FormikDropdown, FormikNumberField } from "../../../../../../shared/components/forms/Formik"
import {
    SegmentedControlOption,
    StandardSegmentedControl,
} from "../../../../../../shared/components/segmentedControl/StandardSegmentedControl"
import { useMoney } from "../../../../../currency/useMoney"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"

import { AdjustmentDirection, AdjustmentUnit, PriceAdjustmentType, RoundingMode } from "../../priceListTypes"
import { AdjustmentExampleRow } from "./AdjustmentExampleRow"
import {
    AdjustPricesFormValues,
    buildRoundingVars,
    MAX_VALUE,
    PreviewRow,
    ROUNDING_LABEL_KEYS,
} from "./adjustPricesLib"

const useStyles = makeStyles()((theme) => ({
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
    fields: {
        alignItems: "flex-start",
    },
    valueField: {
        flex: 1,
    },
    roundingField: {
        flex: 1,
    },
    checkboxCard: {
        padding: ".75rem 1.125rem .75rem .75rem",
        borderRadius: ".625rem",
    },
    checkboxLabel: {
        fontWeight: 600,
    },
    dateField: {
        maxWidth: "16rem",
    },
}))

const ROUNDING_OPTIONS: ReadonlyArray<RoundingMode> = [
    RoundingMode.NONE,
    RoundingMode.QUARTER,
    RoundingMode.HALF,
    RoundingMode.WHOLE,
]

const ROUNDING_HINT_KEYS = {
    [RoundingMode.NONE]: "adjust-price-list-rounding-hint-none",
    [RoundingMode.QUARTER]: "adjust-price-list-rounding-hint-quarter",
    [RoundingMode.HALF]: "adjust-price-list-rounding-hint-half",
    [RoundingMode.WHOLE]: "adjust-price-list-rounding-hint-whole",
} as const

type AdjustmentFieldsProps = Readonly<{
    priceListName: string
    currency: Currency<number>
    minDate: DateTime
    example: PreviewRow | undefined
}>

export function AdjustmentFields({ priceListName, currency, minDate, example }: AdjustmentFieldsProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const currencySymbol = moneyFactory.getLocalizedCurrencySymbol(currency)
    const roundingVars = (mode: RoundingMode) => buildRoundingVars(moneyFactory, currency, mode)
    const { values, setFieldValue } = useFormikContext<AdjustPricesFormValues>()
    const [updateOrdersField, , updateOrdersHelpers] = useField<boolean>("updateExistingOrders")

    const directionOptions: ReadonlyArray<SegmentedControlOption<AdjustmentDirection>> = [
        {
            value: PriceAdjustmentType.INCREASE,
            label: (
                <LocalizedStrict id="adjust-price-list-direction-increase">
                    <span>Raise prices</span>
                </LocalizedStrict>
            ),
            tone: "success",
        },
        {
            value: PriceAdjustmentType.REDUCTION,
            label: (
                <LocalizedStrict id="adjust-price-list-direction-reduction">
                    <span>Lower prices</span>
                </LocalizedStrict>
            ),
            tone: "error",
        },
    ]

    const unitOptions: ReadonlyArray<SegmentedControlOption<AdjustmentUnit>> = [
        {
            value: AdjustmentUnit.PERCENTAGE,
            label: (
                <LocalizedStrict id="adjust-price-list-unit-percentage">
                    <span>%</span>
                </LocalizedStrict>
            ),
        },
        {
            value: AdjustmentUnit.AMOUNT,
            label: (
                <LocalizedStrict id="adjust-price-list-unit-amount" vars={{ currency: currencySymbol }}>
                    <span>{currencySymbol}</span>
                </LocalizedStrict>
            ),
        },
    ]

    return (
        <Stack spacing="1.5rem">
            <LocalizedStrict id="adjust-price-list-base-list-field" attrs={{ label: true, helperText: true }}>
                <TextField
                    label="Take prices over from"
                    helperText="Chosen when you opened the adjustment."
                    value={priceListName}
                    fullWidth
                    disabled
                />
            </LocalizedStrict>

            <StandardSegmentedControl<AdjustmentDirection>
                appearance="tinted"
                fullWidth
                options={directionOptions}
                value={values.adjustmentType}
                onChange={(value) => setFieldValue("adjustmentType", value)}
            />

            <Stack direction="row" spacing="1.5rem" className={classes.fields}>
                <div className={classes.valueField}>
                    <LocalizedStrict id="adjust-price-list-value-field" attrs={{ label: true }}>
                        <FormikNumberField<AdjustPricesFormValues>
                            name="value"
                            label="By how much?"
                            range="positive-decimal"
                            max={MAX_VALUE}
                            decimalScale={2}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <StandardSegmentedControl<AdjustmentUnit>
                                            appearance="track"
                                            size="small"
                                            options={unitOptions}
                                            value={values.unit}
                                            onChange={(unit) => setFieldValue("unit", unit)}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </LocalizedStrict>
                </div>

                <div className={classes.roundingField}>
                    <LocalizedStrict id="adjust-price-list-rounding-field" attrs={{ label: true }}>
                        <FormikDropdown<AdjustPricesFormValues> name="rounding" label="Rounding" fullWidth>
                            {ROUNDING_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    <LocalizedStrict id={ROUNDING_LABEL_KEYS[option]} vars={roundingVars(option)}>
                                        <span>{option}</span>
                                    </LocalizedStrict>
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id={ROUNDING_HINT_KEYS[values.rounding]} vars={roundingVars(values.rounding)}>
                        <Typography variant="caption" className={classes.hint}>
                            Prices keep their exact value.
                        </Typography>
                    </LocalizedStrict>
                </div>
            </Stack>

            {example && <AdjustmentExampleRow example={example} />}

            <Divider />

            <Stack spacing=".5rem" alignItems="flex-start">
                <Stack spacing=".125rem">
                    <LocalizedStrict id="adjust-price-list-existing-orders-title">
                        <Typography variant="subtitle1">Unposted orders</Typography>
                    </LocalizedStrict>
                    {updateOrdersField.value && values.effectiveFrom ? (
                        <LocalizedStrict
                            id="adjust-price-list-existing-orders-note-updated"
                            vars={{ date: dateTimeFactory.formatLongDate(values.effectiveFrom) }}
                        >
                            <Typography variant="body2" className={classes.hint}>
                                Unposted orders delivering from {dateTimeFactory.formatLongDate(values.effectiveFrom)}{" "}
                                get the new prices.
                            </Typography>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="adjust-price-list-existing-orders-note-kept">
                            <Typography variant="body2" className={classes.hint}>
                                Unposted orders keep their current prices.
                            </Typography>
                        </LocalizedStrict>
                    )}
                </Stack>
                <CheckboxCard
                    checked={updateOrdersField.value}
                    onChange={(checked) => updateOrdersHelpers.setValue(checked)}
                    color="success"
                    highlightChecked
                    className={classes.checkboxCard}
                    label={
                        <LocalizedStrict id="adjust-price-list-existing-orders-checkbox">
                            <Typography variant="body2" className={classes.checkboxLabel}>
                                Update these orders too
                            </Typography>
                        </LocalizedStrict>
                    }
                />
            </Stack>

            <div className={classes.dateField}>
                <LocalizedStrict id="adjust-price-list-effective-from-field" attrs={{ label: true }}>
                    <FormikDatePicker<AdjustPricesFormValues, DateTime>
                        name="effectiveFrom"
                        label="Effective from"
                        range="now-to-future"
                        minDate={minDate}
                    />
                </LocalizedStrict>
            </div>
        </Stack>
    )
}
